const SUPPORTED_LANGUAGES = ["en", "fr", "ua", "si"];
const DEFAULT_LANGUAGE = "en";

const sanitizeBasePath = (basePath) => {
  if (!basePath || basePath === "/") {
    return "";
  }
  return basePath.replace(/\/+$/, "");
};

const getBasePath = () => sanitizeBasePath(process.env.PUBLIC_URL || "");

const normalizeLanguage = (language) => {
  if (!language) {
    return null;
  }

  const lowered = language.toLowerCase().trim();
  if (SUPPORTED_LANGUAGES.includes(lowered)) {
    return lowered;
  }

  const short = lowered.split("-")[0];
  return SUPPORTED_LANGUAGES.includes(short) ? short : null;
};

const getBrowserLanguage = () => {
  if (typeof navigator === "undefined") {
    return DEFAULT_LANGUAGE;
  }
  return normalizeLanguage(navigator.language) || DEFAULT_LANGUAGE;
};

const stripBasePath = (pathname) => {
  const basePath = getBasePath();
  if (!basePath) {
    return pathname || "/";
  }
  if (!pathname || pathname === basePath) {
    return "/";
  }
  return pathname.startsWith(basePath) ? pathname.slice(basePath.length) || "/" : pathname;
};

const extractPathLanguage = (pathname) => {
  const cleanPath = stripBasePath(pathname || "/");
  const parts = cleanPath.split("/").filter(Boolean);
  if (!parts.length) {
    return null;
  }
  return normalizeLanguage(parts[0]);
};

const getPathWithoutLanguage = (pathname) => {
  const cleanPath = stripBasePath(pathname || "/");
  const parts = cleanPath.split("/").filter(Boolean);
  if (!parts.length) {
    return "/";
  }

  const firstPartAsLanguage = normalizeLanguage(parts[0]);
  if (firstPartAsLanguage) {
    const remaining = parts.slice(1);
    return remaining.length ? `/${remaining.join("/")}` : "/";
  }

  return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
};

const buildLanguagePath = (language, pathname = "/", search = "", hash = "") => {
  const basePath = getBasePath();
  const normalizedLanguage = normalizeLanguage(language) || DEFAULT_LANGUAGE;
  const restPath = getPathWithoutLanguage(pathname);
  const normalizedRest = restPath === "/" ? "" : restPath;
  const finalPath = `${basePath}/${normalizedLanguage}${normalizedRest}`.replace(/\/{2,}/g, "/");
  return `${finalPath}${search || ""}${hash || ""}`;
};

const updateLanguageInUrl = (language, options = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const { replace = false } = options;
  const targetUrl = buildLanguagePath(
    language,
    window.location.pathname,
    window.location.search,
    window.location.hash
  );

  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (targetUrl === currentUrl) {
    return;
  }

  const stateMethod = replace ? "replaceState" : "pushState";
  window.history[stateMethod](window.history.state, "", targetUrl);
};

const syncLanguageFromUrl = (i18n, options = {}) => {
  if (!i18n || typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }

  const { redirectIfMissing = true } = options;
  const pathLanguage = extractPathLanguage(window.location.pathname);
  const activeLanguage = pathLanguage || getBrowserLanguage();

  if (!pathLanguage && redirectIfMissing) {
    updateLanguageInUrl(activeLanguage, { replace: true });
  }

  if (i18n.language !== activeLanguage) {
    i18n.changeLanguage(activeLanguage);
  }

  return activeLanguage;
};

const switchLanguage = (i18n, language, options = {}) => {
  const normalizedLanguage = normalizeLanguage(language) || DEFAULT_LANGUAGE;
  updateLanguageInUrl(normalizedLanguage, options);
  if (i18n && i18n.language !== normalizedLanguage) {
    i18n.changeLanguage(normalizedLanguage);
  }
};

export {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  normalizeLanguage,
  buildLanguagePath,
  syncLanguageFromUrl,
  switchLanguage
};
